/**
 * This mixin contains logic for driving the Three.js scene for the simple curve lab.
 */

import { mapGetters, mapActions } from "vuex";
import { StepId, CurveConstants, PointName } from "@/data/content";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import { Vector3 } from "three";
import { createMeasurementLine, createLabelDiv } from "@/helpers/ThreeHelpers";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { getCalculatedMidpointCoordinates } from "@/helpers/curveLabHelpers";
const TC_POINT_SECTION_INDEX = 1;
const PI_POINT_SECTION_INDEX = 0;

const simpleCurveSceneMixin = {
  computed: {
    ...mapGetters([
      "getCurrentStepId",
      "getCurrentSectionIndex",
      "mapToDatastore",
      "getPointPositions"
    ])
  },
  mixins: [DatastoreAPI],
  data() {
    return {
      // Only one prism is used at a time, value is null if no prism should be visible in scene
      prismPosition: null
    };
  },
  mounted() {
    console.log("simpleCurveSceneMixin mounted");
    // TODO: If the back tangent marker is not placed, place it
    this.doStepOperations(this.getCurrentStepId);
  },
  methods: {
    ...mapActions([
      "updatePointPosition",
      "updateInstrumentHeight",
      "setAtPointName",
      "setHasMeasuredOnCurrentStep"
    ]),
    //   TODO: move back tangent marker position calculation from store, once we have a more complex calculation
    placeBackTangentMarker() {
      // TODO: calculate marker position and enter position into store
    },
    // Calculate random point near the given position
    getRandomPointNear(position) {
      // maximum 1 meter away from the given position in either direction
      const distance = 1;
      const randomX = position.x + (Math.random() - 0.5) * distance;
      const randomZ = position.z + (Math.random() - 0.5) * distance;
      return new Vector3(randomX, 0, randomZ);
    },
    doStepOperations(stepId) {
      // TODO: implement operations for all steps
      if (stepId === StepId.STAKE_OUT_TC) {
        console.log("placing TC Prism");
        this.prismPosition = this.getRandomPointNear(
          new Vector3(-CurveConstants.T, 0, 0)
        );
        this.rotatePrismToFaceCamera();
      } else if (stepId === StepId.STAKE_OUT_CT) {
        console.log("placing CT Prism");
        // point is T distance away from origin, PI - I counterclockwise from TC
        const ctPosition = new Vector3(-CurveConstants.T, 0, 0);
        const rotation = Math.PI - CurveConstants.I;
        ctPosition.applyAxisAngle(new Vector3(0, 1, 0), rotation);
        this.prismPosition = this.getRandomPointNear(ctPosition);
        this.rotatePrismToFaceCamera();
      } else if (
        stepId?.startsWith("STAKE_OUT_TP") ||
        stepId === StepId.STAKE_OUT_MP ||
        stepId === StepId.STAKE_OUT_CT2
      ) {
        // All the Midpoint stake placement steps have initial prism placement done the same way
        const stakeName = stepId.replace("STAKE_OUT_", "");
        console.log("placing ", stakeName, " Prism");
        const position = getCalculatedMidpointCoordinates(
          stakeName,
          this.getPointPositions.TC,
          this.getPointPositions.PI
        );
        this.prismPosition = this.getRandomPointNear(position);
        this.rotatePrismToFaceCamera();
      } else if (stepId === StepId.MEASURE_ERROR_MP) {
        if (this.measurementLine) {
          // remove the measurement line if one exists
          this.removeMeasurementLine();
        }
        // Store reference to the measurement line for cleanup
        this.measurementLine = createMeasurementLine(
          this.getPointPositions.MP,
          this.getPointPositions.PI,
          this.scene
        );

        const labelDiv = createLabelDiv(
          "Distance Length",
          "black",
          "rgba(255, 255, 255, 0.9)",
          true
        );
        // hide the label initially so it only shows up when the measurement line is done animating
        labelDiv.style.visibility = "hidden";
        const label = new CSS2DObject(labelDiv);

        this.measurementLine.mesh.add(label);
      } else if (stepId === StepId.MEASURE_ERROR_CT2) {
        if (this.measurementLine) {
          // remove the measurement line if one exists
          this.removeMeasurementLine();
        }
        // Store reference to the measurement line for cleanup
        this.measurementLine = createMeasurementLine(
          this.getPointPositions.CT2,
          this.getPointPositions.CT,
          this.scene
        );

        const labelDiv = createLabelDiv(
          "Distance Length",
          "black",
          "rgba(255, 255, 255, 0.9)",
          true
        );
        // hide the label initially so it only shows up when the measurement line is done animating
        labelDiv.style.visibility = "hidden";
        const label = new CSS2DObject(labelDiv);

        this.measurementLine.mesh.add(label);
      }

      // TODO: handle MP (midpoint) stake placement, and error measurement
    },
    updateCurrentPointPosition(position) {
      // Get the current point name from the step id
      const pointName = this.getCurrentStepId.replace("STAKE_OUT_", "");
      this.updatePointPosition({
        pointName: pointName,
        position: position
      });
    },
    removeMeasurementLine: function () {
      if (this.measurementLine) {
        this.scene.remove(this.measurementLine.mesh);
        // Remove the label from the measurement line
        this.measurementLine.mesh.children.forEach((child) => {
          if (child instanceof CSS2DObject) {
            child.element.remove(); // Remove the DOM element
            child.removeFromParent(); // Remove from Three.js scene
          }
        });
        this.measurementLine = null;
      }
    }
  },
  watch: {
    getCurrentStepId: function (newVal, oldVal) {
      // If the user backtracks from a stake placement step, before placing the stake
      // TODO: test behavior when user backtracks from one stake placement step to another stake placement step
      // TODO: handle scenario when user refreshes after placing a stake without marking the step as completed
      if (oldVal?.startsWith("STAKE_OUT")) {
        // reset prism position
        this.prismPosition = null;
      }
      if (oldVal?.startsWith("MEASURE")) {
        // remove the measurement line when the user exits a measurement step
        this.removeMeasurementLine();
      }
      if (newVal === StepId.STAKE_OUT_CT2) {
        // hide the original CT placement
        this.setPointVisibility(PointName.CT, false);
      } else if (oldVal === StepId.STAKE_OUT_CT2) {
        // show the original CT placement
        this.setPointVisibility(PointName.CT, true);
      }
      // Reset the hasMeasuredOnCurrentStep flag when the step changes
      this.setHasMeasuredOnCurrentStep(false);
      this.doStepOperations(newVal);
    },
    getCurrentSectionIndex: function (newVal, oldVal) {
      if (
        newVal === TC_POINT_SECTION_INDEX &&
        oldVal === PI_POINT_SECTION_INDEX
      ) {
        // If the user is changing from the first section to the second section, set the "At" point to TC
        console.log("setting at point to TC");
        this.setAtPointName(PointName.TC);
      } else if (
        newVal === PI_POINT_SECTION_INDEX &&
        oldVal === TC_POINT_SECTION_INDEX
      ) {
        // If the user is changing from the second section to the first section, set the "At" point to PI
        console.log("setting at point to PI");
        this.setAtPointName(PointName.PI);
      }
    }
  }
};

export default simpleCurveSceneMixin;
